/**
 * Snippets Store - Centralized State Management
 *
 * This module provides a centralized store for managing snippets data
 * using react-sweet-state. This replaces the previous pattern of individual hooks
 * (useCreateSnippet, useFetchSnippets) with a single source of truth.
 *
 * Usage:
 * - Wrap your app with <SnippetsProvider> to trigger initial fetch
 * - Use useSnippetsState() for read-only state access
 * - Use useSnippetsActions() when you need to dispatch actions
 * - Use useSnippets() when you need both state and actions
 */
import type {
	Action,
	BoundActions,
	GetState,
	HookActionsFunction,
	HookFunction,
	HookStateFunction,
	SetState,
	Store,
	StoreActionApi,
} from 'react-sweet-state';
import {
	createStore,
	createHook,
	createActionsHook,
	createStateHook,
	defaultRegistry,
} from 'react-sweet-state';

import { onCurrentFetchComplete as onCurrentFetchCompleteUtil } from '../utils/pendingCallbacks.ts';

import type { SnippetsState } from './snippetsStore.types.ts';
import { getInitialState } from './snippetsStore.types.ts';
import { actions } from './actions/index.ts';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { SNIPPET_CATEGORY_IDS } from '../constants/snippetCategories.ts';
import type { SnippetSortOrder } from '../constants/snippetSortOrder.ts';
import type { Snippet } from '../types/snippet.ts';
import type { CreateSnippetOptions } from './actions/actionsHelper.ts';
import type { DeleteSnippetOptions } from './actions/deleteActions.ts';
import type { FavoriteSnippetOptions } from './actions/favoriteActions.ts';
import type { ClaimSnippetShareOptions, ShareSnippetOptions } from './actions/shareActions.ts';
import type { UpdateSnippetOptions } from './actions/updateActions.ts';

export const SnippetsStore: Store<
	SnippetsState,
	{
		setTemplateGallerySelectedSnippetId: (
			id: string | null,
		) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
		setTemplateGalleryCategoryFilter: (
			selectedCategoryId: SNIPPET_CATEGORY_IDS,
		) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
		setTemplateGallerySearchQuery: (
			searchQuery: string,
		) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
		setTemplateGallerySortOrder: (
			sortOrder: SnippetSortOrder,
		) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
		resetTemplateGalleryFilters: () => ({
			getState,
			setState,
		}: StoreActionApi<SnippetsState>) => void;
		recordSnippetInsert: (
			snippetId: string,
		) => ({ setState, getState }: StoreActionApi<SnippetsState>) => void;
		claimSnippetShare: (
			snippetId: string,
			shareId: string,
			options?: ClaimSnippetShareOptions,
		) => ({
			getState,
			setState,
		}: {
			setState: SetState<SnippetsState>;
			getState: GetState<SnippetsState>;
			dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
		}) => Promise<void>;
		shareSnippet: (
			snippetId: string,
			options?: ShareSnippetOptions,
		) => ({
			getState,
			setState,
		}: {
			setState: SetState<SnippetsState>;
			getState: GetState<SnippetsState>;
			dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
		}) => Promise<void>;
		unshareSnippet: (
			snippetId: string,
			options?: ShareSnippetOptions,
		) => ({
			getState,
			setState,
		}: {
			setState: SetState<SnippetsState>;
			getState: GetState<SnippetsState>;
			dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
		}) => Promise<void>;
		favoriteSnippet: (
			snippetId: string,
			options?: FavoriteSnippetOptions,
		) => ({ setState, getState }: StoreActionApi<SnippetsState>) => Promise<boolean>;
		unfavoriteSnippet: (
			snippetId: string,
			options?: FavoriteSnippetOptions,
		) => ({ setState, getState }: StoreActionApi<SnippetsState>) => Promise<boolean>;
		updateSnippet: (
			snippetId: string,
			name: string,
			body: ADFEntity,
			description?: string,
			options?: UpdateSnippetOptions,
		) => (storeApi: StoreActionApi<SnippetsState>) => Promise<Snippet | null>;
		deleteSnippet: (
			snippetId: string,
			options?: DeleteSnippetOptions,
		) => ({ getState, setState }: StoreActionApi<SnippetsState>) => Promise<void>;
		createSnippet: (
			name: string,
			body: ADFEntity,
			description?: string,
			options?: CreateSnippetOptions,
		) => (storeApi: StoreActionApi<SnippetsState>) => Promise<Snippet | null>;
		fetchSnippets: (
			locale?: string,
		) => ({ getState, setState }: StoreActionApi<SnippetsState>) => Promise<Snippet[]>;
		clearCache: () => ({ setState }: StoreActionApi<SnippetsState>) => void;
	}
> = createStore({
	initialState: getInitialState(),
	actions,
	name: 'SnippetsStore',
});

/** Hook to access both state and actions - use when you need full store access */
export const useSnippets: HookFunction<
	SnippetsState,
	BoundActions<
		SnippetsState,
		{
			setTemplateGallerySelectedSnippetId: (
				id: string | null,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
			setTemplateGalleryCategoryFilter: (
				selectedCategoryId: SNIPPET_CATEGORY_IDS,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
			setTemplateGallerySearchQuery: (
				searchQuery: string,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
			setTemplateGallerySortOrder: (
				sortOrder: SnippetSortOrder,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
			resetTemplateGalleryFilters: () => ({
				getState,
				setState,
			}: StoreActionApi<SnippetsState>) => void;
			recordSnippetInsert: (
				snippetId: string,
			) => ({ setState, getState }: StoreActionApi<SnippetsState>) => void;
			claimSnippetShare: (
				snippetId: string,
				shareId: string,
				options?: ClaimSnippetShareOptions,
			) => ({
				getState,
				setState,
			}: {
				setState: SetState<SnippetsState>;
				getState: GetState<SnippetsState>;
				dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
			}) => Promise<void>;
			shareSnippet: (
				snippetId: string,
				options?: ShareSnippetOptions,
			) => ({
				getState,
				setState,
			}: {
				setState: SetState<SnippetsState>;
				getState: GetState<SnippetsState>;
				dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
			}) => Promise<void>;
			unshareSnippet: (
				snippetId: string,
				options?: ShareSnippetOptions,
			) => ({
				getState,
				setState,
			}: {
				setState: SetState<SnippetsState>;
				getState: GetState<SnippetsState>;
				dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
			}) => Promise<void>;
			favoriteSnippet: (
				snippetId: string,
				options?: FavoriteSnippetOptions,
			) => ({ setState, getState }: StoreActionApi<SnippetsState>) => Promise<boolean>;
			unfavoriteSnippet: (
				snippetId: string,
				options?: FavoriteSnippetOptions,
			) => ({ setState, getState }: StoreActionApi<SnippetsState>) => Promise<boolean>;
			updateSnippet: (
				snippetId: string,
				name: string,
				body: ADFEntity,
				description?: string,
				options?: UpdateSnippetOptions,
			) => (storeApi: StoreActionApi<SnippetsState>) => Promise<Snippet | null>;
			deleteSnippet: (
				snippetId: string,
				options?: DeleteSnippetOptions,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => Promise<void>;
			createSnippet: (
				name: string,
				body: ADFEntity,
				description?: string,
				options?: CreateSnippetOptions,
			) => (storeApi: StoreActionApi<SnippetsState>) => Promise<Snippet | null>;
			fetchSnippets: (
				locale?: string,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => Promise<Snippet[]>;
			clearCache: () => ({ setState }: StoreActionApi<SnippetsState>) => void;
		}
	>,
	void
> = createHook(SnippetsStore);

/** Hook to access only actions - use when component only dispatches actions */
export const useSnippetsActions: HookActionsFunction<
	BoundActions<
		SnippetsState,
		{
			setTemplateGallerySelectedSnippetId: (
				id: string | null,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
			setTemplateGalleryCategoryFilter: (
				selectedCategoryId: SNIPPET_CATEGORY_IDS,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
			setTemplateGallerySearchQuery: (
				searchQuery: string,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
			setTemplateGallerySortOrder: (
				sortOrder: SnippetSortOrder,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => void;
			resetTemplateGalleryFilters: () => ({
				getState,
				setState,
			}: StoreActionApi<SnippetsState>) => void;
			recordSnippetInsert: (
				snippetId: string,
			) => ({ setState, getState }: StoreActionApi<SnippetsState>) => void;
			claimSnippetShare: (
				snippetId: string,
				shareId: string,
				options?: ClaimSnippetShareOptions,
			) => ({
				getState,
				setState,
			}: {
				setState: SetState<SnippetsState>;
				getState: GetState<SnippetsState>;
				dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
			}) => Promise<void>;
			shareSnippet: (
				snippetId: string,
				options?: ShareSnippetOptions,
			) => ({
				getState,
				setState,
			}: {
				setState: SetState<SnippetsState>;
				getState: GetState<SnippetsState>;
				dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
			}) => Promise<void>;
			unshareSnippet: (
				snippetId: string,
				options?: ShareSnippetOptions,
			) => ({
				getState,
				setState,
			}: {
				setState: SetState<SnippetsState>;
				getState: GetState<SnippetsState>;
				dispatch: <T extends Action<SnippetsState, any, any>>(actionThunk: T) => ReturnType<T>;
			}) => Promise<void>;
			favoriteSnippet: (
				snippetId: string,
				options?: FavoriteSnippetOptions,
			) => ({ setState, getState }: StoreActionApi<SnippetsState>) => Promise<boolean>;
			unfavoriteSnippet: (
				snippetId: string,
				options?: FavoriteSnippetOptions,
			) => ({ setState, getState }: StoreActionApi<SnippetsState>) => Promise<boolean>;
			updateSnippet: (
				snippetId: string,
				name: string,
				body: ADFEntity,
				description?: string,
				options?: UpdateSnippetOptions,
			) => (storeApi: StoreActionApi<SnippetsState>) => Promise<Snippet | null>;
			deleteSnippet: (
				snippetId: string,
				options?: DeleteSnippetOptions,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => Promise<void>;
			createSnippet: (
				name: string,
				body: ADFEntity,
				description?: string,
				options?: CreateSnippetOptions,
			) => (storeApi: StoreActionApi<SnippetsState>) => Promise<Snippet | null>;
			fetchSnippets: (
				locale?: string,
			) => ({ getState, setState }: StoreActionApi<SnippetsState>) => Promise<Snippet[]>;
			clearCache: () => ({ setState }: StoreActionApi<SnippetsState>) => void;
		}
	>
> = createActionsHook(SnippetsStore);

/** Hook to access only state - use for read-only state consumption */
export const useSnippetsState: HookStateFunction<SnippetsState, void> =
	createStateHook(SnippetsStore);

/**
 * Gets the current snippets state without subscribing to updates.
 * Use this when you need a one-time snapshot of the state outside of React's render cycle,
 * or when you want to avoid re-renders when the state changes.
 */
export const getSnippetsStateSnapshot = (): SnippetsState => {
	const storeInstance = defaultRegistry.getStore(SnippetsStore);
	return storeInstance.storeState.getState();
};

export const recordSnippetInsert = (snippetId: string): void => {
	defaultRegistry.getStore(SnippetsStore).actions.recordSnippetInsert(snippetId);
};

export const onCurrentFetchComplete: (callback: (snippets: Snippet[]) => void) => void =
	onCurrentFetchCompleteUtil;
